import React, {
  useState, useEffect, useRef, useCallback,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import { datadogRum } from '@datadog/browser-rum'
import useCart from '@/public/hooks/use-cart'
import { accessToken } from '@/shared/helpers/CSRF'
import { useForm, FormProvider } from 'react-hook-form'
import Form from '@/public/checkout/Form'
import Cart from '@/public/checkout/Cart'
import ExpressCheckout from '@/public/checkout/ExpressCheckout'
import Modal from '@/public/Modal'

import {
  fetchCustomer,
  setCustomer,
  setError,
  setShippingCosts,
  selectCustomer,
  setLoading,
} from '@/public/redux/cartSlice'
import axios from '../../../lib/httpClient'
import { gaPurchase } from '../../shared/helpers/googleAnalytics'

// The order a checkout attempt created, kept in localStorage beside the cart it
// was built from (useCart keeps the cart's uuid there). Any later submit for
// that cart sends it back, so the server reopens that order rather than create
// a second one holding stock -- after a decline, a reload, a redirect-based
// payment method returning here, or a confirmPayment that threw. The server
// reopens it only for this same cart, so an entry left by a finished checkout,
// whose cart was cleared, is ignored. Storage errors (a full or blocked store)
// are swallowed: losing the entry only costs a fresh order, while throwing
// here would stop the payment.
const CHECKOUT_ORDER_KEY = 'checkoutOrder'

function rememberCheckoutOrder(cartUuid, orderId) {
  try {
    window.localStorage.setItem(CHECKOUT_ORDER_KEY, JSON.stringify({ cartUuid, orderId }))
  } catch (storageError) {
    // Nothing to do: the next submit starts a fresh order.
  }
}

function checkoutOrderFor(cartUuid) {
  try {
    const saved = JSON.parse(window.localStorage.getItem(CHECKOUT_ORDER_KEY))
    return saved?.cartUuid === cartUuid ? saved.orderId : null
  } catch (storageError) {
    return null
  }
}

function getCartTotal(cart) {
  const totalAfterSavings = parseFloat(cart?.calculated_total_after_savings) || 0
  const sponsoredAllowance = parseFloat(cart?.sponsored_allowance) || 0
  const supplementInvoicingAmount = parseFloat(cart?.supplement_invoicing_amount) || 0

  if (totalAfterSavings <= 0) {
    return 0
  }

  return Math.max(0, totalAfterSavings - sponsoredAllowance - supplementInvoicingAmount)
}

// ------------------------------------------------------------------------------
// CHECKOUT FLOW
// ------------------------------------------------------------------------------
// react-hook-form validates user info and shipping fields before form is submitted
//
// The form is then submitted to /api/orders which creates:
//   1. stripe customer
//   2. stripe payment intent
//   3. order with its payment processing
//
// stripe.confirmPayment is called with data from the api call response above
//
// A webhook is received and processed in order.rb after_payment_intent_scceeded!,
// order is updated to paid status.
// ------------------------------------------------------------------------------

export default function Checkout({
  knownCustomer,
  states,
  provinces,
  countries,
  store_default_path,
  confirmation_path,
  cc_logos_url,
  terms_policy_modal_id,
  shipping_costs,
  debug = false,
  activeDiscountCodePresent,
  membershipAgreementUrl,
  hipaaPrivacyTermsUrl,
  monthlyMembership,
  membershipPlansEnabled = false,
  annualMembership,
  userSponsoredAllowance,
  virtualVisits,
}) {
  const formRef = useRef()
  const formMethods = useForm()
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const customer = useSelector(selectCustomer)
  const { cart, isLoading, mutate } = useCart()
  const [selectedState, setSelectedState] = useState('')
  // Holds the validated order args while the mixed-lab-company confirm modal is
  // open; non-null means the modal is showing and submission is paused on it.
  const [pendingMixedLabOrder, setPendingMixedLabOrder] = useState(null)
  // Synchronous re-entrancy guard for the confirm-modal "Yes" button. State
  // updates are async, so a fast double-click can have both clicks observe the
  // pre-update state and each fire submitOrder, creating duplicate orders/charges.
  // A ref flips synchronously on the first click, so the second click no-ops.
  // We also drive the modal's disabled/submitting state off this for a visible
  // guard. It resets on cancel and on a failed confirmed submit so a genuine
  // later checkout attempt still works.
  const confirmSubmittingRef = useRef(false)
  const [confirmSubmitting, setConfirmSubmitting] = useState(false)
  // Set by the payment field when Stripe cannot draw it, and read by Place
  // Order: an order with no way to pay for it is not one to offer. It latches,
  // so Place Order reads it only while the cart still needs paying for -- a
  // cart that drops to $0 after the field failed no longer needs the field.
  const [paymentUnavailable, setPaymentUnavailable] = useState(false)
  const markPaymentUnavailable = useCallback(() => setPaymentUnavailable(true), [])
  const showExpressCheckout = cart?.contents?.length > 0 && cart?.calculated_total_after_savings > 0

  if (!window.location.pathname.includes(localStorage.getItem('cart'))) {
    window.location = store_default_path
  }

  /**
   * Mount/Unmount
   */
  useEffect(() => {
    if (!knownCustomer && cart?.user_id) {
      dispatch(fetchCustomer(cart?.user_id, accessToken))
    } else {
      dispatch(setCustomer(knownCustomer))
    }

    dispatch(setShippingCosts(shipping_costs))
  }, [cart?.user_id])

  const submitOrder = useCallback(async (customerForm, policyAgreements, { confirmedMixedLabCompany = false } = {}) => {
    // Gate mixed-lab-company carts on the confirm modal here — before any
    // loading/submitting state — so EVERY submission entrypoint (the form's
    // handleSubmit AND Express Checkout, which calls submitOrder directly) is
    // gated. confirmedMixedLabCompany bypasses the gate so clicking "Yes" in the
    // modal actually submits instead of reopening it. The flag is server-computed
    // and already false when the labcorp integration is off, so non-mixed carts
    // and disabled-integration carts never see the modal.
    if (cart?.mixed_lab_company && !confirmedMixedLabCompany) {
      setPendingMixedLabOrder({ customerForm, policyAgreements })
      return
    }

    if (debug && console) { console.log('Order submitted with data:', customerForm, policyAgreements) }
    dispatch(setLoading(true))

    const cartTotal = getCartTotal(cart)

    // Only validate payment elements if payment is required
    if (cartTotal > 0) {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        if (debug && console) { console.log(submitError) }
        dispatch(setError(submitError.message))
        dispatch(setLoading(false))
        return
      }
    }

    try {
      const { data: { order, payment_intent, stripe_customer } } = await axios({
        method: 'post',
        url: '/api/orders',
        headers: { 'X-CSRF-Token': accessToken },
        data: {
          customer: {
            ...customerForm,
          },
          policy_agreements: policyAgreements,
          order: {
            cart_id: cart.uuid,
            order_id: checkoutOrderFor(cart.uuid),
          },
        },
      })

      // No payment intent for free orders
      if (payment_intent) {
        // Before confirming: every way this attempt can end short of a payment
        // has to leave the next submit able to find this order.
        rememberCheckoutOrder(cart.uuid, order.id)
        const { error: confirmError } = await stripe.confirmPayment({
          elements,
          clientSecret: payment_intent.client_secret,
          confirmParams: {
            shipping: stripe_customer.shipping,
            payment_method_data: {
              billing_details: {
                name: stripe_customer.name,
                email: stripe_customer.email,
                phone: stripe_customer.phone,
                address: stripe_customer.address,
              },
            },
            return_url: `${window.location.origin}${confirmation_path}/${order.id}`,
          },
        })

        if (confirmError) {
          if (debug && console) { console.log(confirmError) }
          // Show the customer their error FIRST. Telemetry comes after, and
          // guarded, so it can never strand them on a spinner with no message
          // -- the exact failure shape this instrumentation exists to catch.
          dispatch(setError(confirmError.message))
          dispatch(setLoading(false))
          // The customer does see this failure -- setError renders it beside the
          // form. What was missing is telemetry: confirmPayment reports failure
          // by RESOLVING with an error rather than throwing, so it never becomes
          // an unhandled exception and reached no Datadog surface at all.
          // Carry Stripe's own classification plus the cart shape that decides
          // which intent gets confirmed -- never customer details.
          //
          // RUM only initialises when window.datadogRumSampleRate > 0
          // (scripts/datadog.js), so this is inert wherever RUM is off.
          try {
            datadogRum.addError(new Error(confirmError.message), {
              source: 'stripe.confirmPayment',
              stripeErrorType: confirmError.type,
              stripeErrorCode: confirmError.code,
              stripeDeclineCode: confirmError.decline_code,
              orderId: order.id,
              renderTimeContainsMembership: cart?.contains_a_membership,
            })
          } catch (telemetryError) {
            if (debug && console) { console.log(telemetryError) }
          }
        }
      } else {
        window.location = `${confirmation_path}/${order.id}`
      }
    } catch (orderError) {
      if (debug && console) { console.log(orderError?.response || orderError) }

      if (orderError?.response?.status === 404) {
        window.location = `${store_default_path}/supplements?cart_flash_notice=true`
      }

      // A refusal that corrected the cart (a lab only available through an
      // employer's plan) returns the member to that corrected cart, notice and all.
      if (orderError?.response?.data?.cart_changed) {
        mutate()
      }

      dispatch(setError(orderError?.response?.data?.message))
      dispatch(setLoading(false))
    }
  }, [cart, debug, elements, stripe, dispatch, mutate, confirmation_path, store_default_path])

  const handleSubmit = async (data) => {
    submitOrder(data.customer, data.policy_agreements)
  }

  const handleConfirmMixedLabOrder = useCallback(async () => {
    // Synchronous re-entrancy guard: the first click flips the ref before any
    // async/state work, so a rapid second click bails here instead of firing a
    // duplicate submitOrder. (Reading pendingMixedLabOrder alone is unsafe — it
    // clears asynchronously, so both clicks can still see it non-null.)
    if (confirmSubmittingRef.current || !pendingMixedLabOrder) { return }
    confirmSubmittingRef.current = true
    setConfirmSubmitting(true)

    const { customerForm, policyAgreements } = pendingMixedLabOrder
    setPendingMixedLabOrder(null)

    try {
      await submitOrder(customerForm, policyAgreements, { confirmedMixedLabCompany: true })
    } finally {
      // On success the page navigates away; on a failed/cancelled submit we
      // release the guard so a genuine later checkout attempt can proceed.
      confirmSubmittingRef.current = false
      setConfirmSubmitting(false)
    }
  }, [pendingMixedLabOrder, submitOrder])

  const handleCancelMixedLabOrder = useCallback(() => {
    confirmSubmittingRef.current = false
    setConfirmSubmitting(false)
    setPendingMixedLabOrder(null)
  }, [])

  if (isLoading) { return null }
  if (!cart) {
    window.location = store_default_path
    return null
  }

  return (
    <FormProvider {...formMethods}>
      <section className="pt-4 pt-xl-5 mt-2 mt-lg-4 mt-xl-5">
        <div className="container">
          <form
            id="stripe-form"
            ref={formRef}
            onSubmit={formMethods.handleSubmit(handleSubmit, (errors) => {
              if (debug && console) {
                console.log('Checkout validation errors:', errors)
              }
            })}
          >
            <input type="hidden" value={cart.uuid} {...formMethods.register('order.cart_uuid')} />

            <div className="row justify-content-between">
              <div className="col-lg-6 col-xl-5 checkout-col">
                <h2 className="underline mb-5">Checkout</h2>
                {showExpressCheckout && (
                  <ExpressCheckout
                    cart={cart}
                    submitOrder={submitOrder}
                    contents={cart?.contents}
                    terms_policy_modal_id={terms_policy_modal_id}
                    membershipAgreementUrl={membershipAgreementUrl}
                    hipaaPrivacyTermsUrl={hipaaPrivacyTermsUrl}
                    customer={customer}
                  />
                )}
                <Form
                  setSelectedState={setSelectedState}
                  loadingCustomerAsync={!knownCustomer && cart.user_id}
                  customer={customer}
                  states={states}
                  provinces={provinces}
                  countries={countries}
                  cc_logos_url={cc_logos_url}
                  formMethods={formMethods}
                  cart={cart}
                  membershipAgreementUrl={membershipAgreementUrl}
                  hipaaPrivacyTermsUrl={hipaaPrivacyTermsUrl}
                  onPaymentUnavailable={markPaymentUnavailable}
                />
              </div>

              <Cart
                paymentUnavailable={paymentUnavailable && getCartTotal(cart) > 0}
                selectedState={selectedState}
                termsPolicyModalId={terms_policy_modal_id}
                activeDiscountCodePresent={activeDiscountCodePresent}
                monthlyMembership={monthlyMembership}
                membershipPlansEnabled={membershipPlansEnabled}
                annualMembership={annualMembership}
                userSponsoredAllowance={userSponsoredAllowance}
                virtualVisits={virtualVisits}
              />
            </div>
          </form>
        </div>
      </section>

      {pendingMixedLabOrder && (
        <Modal
          title="Confirm your order"
          confirmText="Yes"
          cancelText="No"
          onConfirm={handleConfirmMixedLabOrder}
          onCancel={handleCancelMixedLabOrder}
          disabled={confirmSubmitting}
          closeOnEscape
        >
          <p className="mb-0">
            This order includes tests from multiple lab companies. Continue placing your order?
          </p>
        </Modal>
      )}
    </FormProvider>
  )
}
